import React, { useState } from 'react';
import { Plus, Trash2, Copy, Anchor, Sparkles, ChevronDown, ChevronUp, MapPin, Ship } from 'lucide-react';
import { PageSection } from '../../../../types';

interface YachtMarinasEditorProps {
  section: PageSection;
  onUpdateContent: (updates: any) => void;
}

export const YachtMarinasEditor: React.FC<YachtMarinasEditorProps> = ({ section, onUpdateContent }) => {
  const content = section.content || {};
  const marinas: any[] = content.marinas || content.items || [
    {
      id: 'm1',
      name: 'D-Marin Göcek',
      location: 'Göcek, Muğla',
      coordinates: "36°45'N 28°56'E",
      berthCapacity: 400,
      maxDraft: '6m',
      maxLoa: '70m',
      coverImage: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?q=80&w=800&auto=format&fit=crop',
    },
    {
      id: 'm2',
      name: 'Yalıkavak Marina',
      location: 'Bodrum, Muğla',
      coordinates: "37°06'N 27°17'E",
      berthCapacity: 620,
      maxDraft: '12m',
      maxLoa: '140m',
      coverImage: 'https://images.unsplash.com/photo-1569263979104-865ab7cd8d17?q=80&w=800&auto=format&fit=crop',
    },
  ];

  const [activeMarinaId, setActiveMarinaId] = useState<string | null>(marinas[0]?.id || null);

  const handleUpdateMarina = (index: number, updates: any) => {
    const next = [...marinas];
    next[index] = { ...next[index], ...updates };
    onUpdateContent({ marinas: next, items: next });
  };

  const handleAddMarina = () => {
    const newMarina = {
      id: `marina_${Date.now().toString(36)}`,
      name: `Yeni Marina ${marinas.length + 1}`,
      location: 'Fethiye, Muğla',
      coordinates: "36°37'N 29°06'E",
      berthCapacity: 300,
      maxDraft: '5m',
      maxLoa: '50m',
      coverImage: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?q=80&w=800&auto=format&fit=crop',
    };
    const next = [...marinas, newMarina];
    onUpdateContent({ marinas: next, items: next });
    setActiveMarinaId(newMarina.id);
  };

  const handleDuplicateMarina = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const source = marinas[index];
    const duplicated = {
      ...JSON.parse(JSON.stringify(source)),
      id: `marina_${Date.now().toString(36)}`,
      name: `${source.name} (Kopya)`,
    };
    const next = [...marinas];
    next.splice(index + 1, 0, duplicated);
    onUpdateContent({ marinas: next, items: next });
    setActiveMarinaId(duplicated.id);
  };

  const handleDeleteMarina = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const next = marinas.filter((_, i) => i !== index);
    onUpdateContent({ marinas: next, items: next });
    if (activeMarinaId === marinas[index]?.id) {
      setActiveMarinaId(next[0]?.id || null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Headings */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
          <h4 className="text-xs font-bold text-slate-200">Marinalar & Limanlar Başlıkları</h4>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Üst Rozet (Badge)</label>
          <input
            type="text"
            value={content.badge || 'BAĞLAMA LİMANLARI & MARİNALAR'}
            onChange={(e) => onUpdateContent({ badge: e.target.value })}
            className="w-full px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-xs text-slate-200 focus:border-cyan-500 outline-none"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Ana Başlık</label>
          <input
            type="text"
            value={content.title || ''}
            onChange={(e) => onUpdateContent({ title: e.target.value })}
            placeholder="Limanlarımız..."
            className="w-full px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-xs text-slate-200 focus:border-cyan-500 outline-none"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Açıklama Metni</label>
          <textarea
            rows={2}
            value={content.description || ''}
            onChange={(e) => onUpdateContent({ description: e.target.value })}
            className="w-full px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-xs text-slate-200 focus:border-cyan-500 outline-none resize-none"
          />
        </div>
      </div>

      {/* Marinas List */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Anchor className="w-4 h-4 text-cyan-400" />
            <span className="text-xs font-bold text-slate-200">Marinalar ({marinas.length})</span>
          </div>
          <button
            type="button"
            onClick={handleAddMarina}
            className="px-2.5 py-1 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-[11px] font-bold flex items-center gap-1 transition"
          >
            <Plus className="w-3 h-3" />
            Marina Ekle
          </button>
        </div>

        <div className="space-y-2">
          {marinas.map((marina, idx) => {
            const isExpanded = activeMarinaId === marina.id;
            return (
              <div
                key={marina.id || idx}
                className="rounded-xl border border-slate-700/70 bg-slate-900/80 overflow-hidden text-xs"
              >
                {/* Item header */}
                <div
                  onClick={() => setActiveMarinaId(isExpanded ? null : marina.id)}
                  className="p-3 flex items-center justify-between cursor-pointer hover:bg-slate-800/50 transition select-none"
                >
                  <div className="flex items-center gap-2.5 min-w-0 pr-2">
                    <span className="w-5 h-5 rounded-md bg-cyan-500/20 text-cyan-400 font-bold text-[10px] flex items-center justify-center shrink-0">
                      {idx + 1}
                    </span>
                    <span className="font-bold text-slate-200 truncate">
                      {marina.name || `Marina #${idx + 1}`}
                    </span>
                    <span className="text-[10px] text-slate-400 shrink-0 font-medium">
                      ({marina.location || 'Konum'})
                    </span>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={(e) => handleDuplicateMarina(idx, e)}
                      title="Kopyala"
                      className="p-1 rounded text-slate-400 hover:text-cyan-300 transition"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDeleteMarina(idx, e)}
                      title="Sil"
                      className="p-1 rounded text-slate-400 hover:text-rose-400 transition"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-slate-400 ml-1" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400 ml-1" />
                    )}
                  </div>
                </div>

                {/* Expanded Fields */}
                {isExpanded && (
                  <div className="p-3 border-t border-slate-800 space-y-3 bg-slate-950/40">
                    <div>
                      <label className="text-[10px] font-bold text-slate-400 uppercase">Marina Adı</label>
                      <input
                        type="text"
                        value={marina.name || ''}
                        onChange={(e) => handleUpdateMarina(idx, { name: e.target.value })}
                        className="w-full mt-1 px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-slate-200 text-xs focus:border-cyan-500 outline-none"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase">Konum</label>
                        <input
                          type="text"
                          value={marina.location || ''}
                          onChange={(e) => handleUpdateMarina(idx, { location: e.target.value })}
                          className="w-full mt-1 px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-slate-200 text-xs focus:border-cyan-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase">Koordinat</label>
                        <input
                          type="text"
                          value={marina.coordinates || ''}
                          onChange={(e) => handleUpdateMarina(idx, { coordinates: e.target.value })}
                          className="w-full mt-1 px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-slate-200 text-xs focus:border-cyan-500 outline-none"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase">Kapasite</label>
                        <input
                          type="number"
                          value={marina.berthCapacity || 400}
                          onChange={(e) => handleUpdateMarina(idx, { berthCapacity: Number(e.target.value) })}
                          className="w-full mt-1 px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-slate-200 text-xs focus:border-cyan-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase">Max Draft</label>
                        <input
                          type="text"
                          value={marina.maxDraft || '6m'}
                          onChange={(e) => handleUpdateMarina(idx, { maxDraft: e.target.value })}
                          className="w-full mt-1 px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-slate-200 text-xs focus:border-cyan-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase">Max LOA</label>
                        <input
                          type="text"
                          value={marina.maxLoa || '70m'}
                          onChange={(e) => handleUpdateMarina(idx, { maxLoa: e.target.value })}
                          className="w-full mt-1 px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-slate-200 text-xs focus:border-cyan-500 outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] font-bold text-slate-400 uppercase">Fotoğraf URL'si</label>
                      <input
                        type="url"
                        value={marina.coverImage || ''}
                        onChange={(e) => handleUpdateMarina(idx, { coverImage: e.target.value })}
                        className="w-full mt-1 px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-slate-200 text-xs focus:border-cyan-500 outline-none"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
